import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useMaybeRoomContext} from '@livekit/components-react';
import {useIntl} from 'react-intl';
import {useGroupCallInvite, useGroupCallState} from '../../groupCalls/hooks/useGroupCall';
import {CallMemberDirectoryContext} from '../../groupCalls/components/callContext';
import {CallBubble} from '../components/CallBubble';
import {CallSurfaceBoundary} from '../components/CallSurfaceBoundary';
import {usePictureInPicture} from '../hooks/usePictureInPicture';
import type {RingTargetState} from '@phonehq/rtc-core';
import type {GroupCallLeg} from '../model/types';
import {selectCallSurfaceState, setPanel, togglePanel} from '../store/callSurfaceSlice';
import {groupActions, type ShareFailure} from './groupActions';
import {GroupActiveBubble, GroupAddPeopleBubble, GroupJoiningBubble, GroupPeopleBubble} from './GroupBubbleViews';
import {GroupPipView} from './GroupPipView';
import {CameraPreview, DevicesDialog, HowToAllowDialog, ShareProblemCard, ShareStartDialog} from './GroupDialogs';
import {GroupStage} from './GroupStage';
import {GroupToasts} from './GroupToasts';
import {useGroupControls} from './useGroupControls';
import {useCallPeople} from './useCallPeople';
import {useInvitees} from './useInvitees';
import {useAloneAutoLeave} from './useAloneAutoLeave';
import {InviteConfirmDialog} from './GroupInvite';
import {groupSurfaceMessages as m} from './groupMessages';
import {ReliefScope} from '../videoRelief/videoReliefStore';
import {useGroupVideoRelief} from '../videoRelief/useVideoRelief';

const PIP_SIZE = {width: 420, height: 440};
const NO_INVITEES: readonly RingTargetState[] = [];

let minimizedCall: string | undefined;

const minimizeOnCallStart = (callKey: string): void => {
    if (minimizedCall !== callKey) {
        minimizedCall = callKey;
        groupActions.minimize();
    }
};

export const GroupCallSurface = ({leg, expanded}: {leg: GroupCallLeg; expanded: boolean}) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const state = useGroupCallState();
    const directory = useContext(CallMemberDirectoryContext);
    const {panel} = useSelector(selectCallSurfaceState);
    const [shareOpen, setShareOpen] = useState(false);
    const [devicesOpen, setDevicesOpen] = useState(false);
    const [howTo, setHowTo] = useState<'microphone' | 'camera'>();
    const [shareProblem, setShareProblem] = useState<ShareFailure>();
    const [conflictWith, setConflictWith] = useState<string>();
    const [confirmingInvite, setConfirmingInvite] = useState(false);
    const invite = useGroupCallInvite();
    const pip = usePictureInPicture(PIP_SIZE);

    const local = state.participants.find(view => view.isLocal);
    const handRaised = local?.handRaised ?? false;
    const remoteSharer = state.participants.find(view => !view.isLocal && view.screenSharing);
    const otherSharer = remoteSharer ? directory(remoteSharer.userId).name : undefined;
    const addOpen = panel === 'add' && !!state.roomId;
    const peopleOpen = panel === 'people' || addOpen;
    const people = useCallPeople(state);
    const inCall = useMemo(() => new Set(people.map(person => person.userId)), [people]);
    const alone = people.every(person => person.isLocal);
    const invitees = useInvitees(state.roomId, inCall, peopleOpen || alone, leg.sessionStartedAt ?? leg.connectedAt);
    const waiting = alone ? invitees : NO_INVITEES;
    const autoLeave = useAloneAutoLeave(alone, waiting, groupActions.leave);
    const canAddPeople = leg.capabilities.has('addPeople') && !!state.roomId;
    const openAddPeople = canAddPeople ? () => dispatch(setPanel('add')) : undefined;
    const joining = leg.phase === 'calling';
    const leaving = state.phase === 'leaving';
    const room = useMaybeRoomContext();
    useGroupVideoRelief(leg.id, room, leg.capabilities.has('camera'), local?.connectionQuality, state.phase === 'joined');

    useEffect(() => {
        minimizeOnCallStart(`${leg.id}:${leg.connectedAt ?? 0}`);
    }, [leg.id, leg.connectedAt]);

    useEffect(() => () => {
        dispatch(setPanel('none'));
    }, [dispatch]);

    useEffect(() => {
        if (!invite) {
            setConfirmingInvite(false);
        }
    }, [invite]);

    useEffect(() => {
        if (state.local.screenSharing && otherSharer) {
            groupActions.stopScreenShare();
            setConflictWith(otherSharer);
        }
    }, [otherSharer, state.local.screenSharing]);

    const startShare = useCallback(async () => {
        setShareOpen(false);
        setShareProblem(await groupActions.startScreenShare());
    }, []);

    const controls = useGroupControls(leg, {
        iconSize: expanded ? 24 : 20,
        busy: joining || leaving,
        peopleOpen,
        otherSharer,
        onTogglePeople: () => dispatch(togglePanel('people')),
        onOpenShare: () => setShareOpen(true),
        onOpenDevices: () => setDevicesOpen(true),
        onHowToAllow: () => setHowTo(state.local.deviceIssues?.microphone === 'blocked' ? 'microphone' : 'camera'),
        onAddPeople: openAddPeople,
    });

    const bubbleContent = joining || leaving
        ? <GroupJoiningBubble leg={leg} leaving={leaving}/>
        : addOpen && state.roomId
            ? <GroupAddPeopleBubble roomId={state.roomId} people={people} invitees={invitees} onBack={() => dispatch(setPanel('people'))}/>
            : peopleOpen
            ? <GroupPeopleBubble people={people} invitees={invitees} onBack={() => dispatch(setPanel('none'))} onAddPeople={openAddPeople}/>
            : (
                <GroupActiveBubble
                    leg={leg}
                    waiting={waiting}
                    autoLeave={autoLeave}
                    invite={invite}
                    onDeclineInvite={groupActions.declineInvite}
                    onAcceptInvite={() => setConfirmingInvite(true)}
                    sharer={otherSharer}
                    controls={controls}
                    handRaised={handRaised}
                    onPopOut={pip.open}
                    onOpenDevices={() => setDevicesOpen(true)}
                />
            );

    return (
        <ReliefScope scope={leg.id}>
        <CallSurfaceBoundary resetKey={leg.id}>
            {expanded && !joining && !leaving ? (
                <GroupStage
                    leg={leg}
                    state={state}
                    controls={controls}
                    handRaised={handRaised}
                    peopleOpen={peopleOpen}
                    addOpen={addOpen}
                    people={people}
                    invitees={invitees}
                    waiting={waiting}
                    alone={alone}
                    autoLeave={autoLeave}
                    invite={invite}
                    onDeclineInvite={groupActions.declineInvite}
                    onAcceptInvite={() => setConfirmingInvite(true)}
                    onClosePeople={() => dispatch(setPanel('none'))}
                    onAddPeople={openAddPeople}
                    onBackFromAdd={() => dispatch(setPanel('people'))}
                    onPopOut={pip.open}
                />
            ) : (
                <CallBubble tone="call" label={intl.formatMessage(m.teamCallRegion, {count: leg.participantCount})} testId="group-call-bubble">
                    {bubbleContent}
                </CallBubble>
            )}
            {pip.window && createPortal(
                <GroupPipView
                    leg={leg}
                    controls={controls}
                    handRaised={handRaised}
                    onReturnToCall={() => {
                        pip.close();
                        groupActions.expand();
                        window.focus();
                    }}
                />,
                pip.window.document.body,
            )}
            <GroupToasts
                issues={state.local.deviceIssues}
                conflictWith={conflictWith}
                onConflictShown={() => setConflictWith(undefined)}
                onHowToAllow={setHowTo}
                onOpenDevices={() => setDevicesOpen(true)}
            />
            {shareOpen && <ShareStartDialog onCancel={() => setShareOpen(false)} onContinue={() => void startShare()}/>}
            {shareProblem && <ShareProblemCard problem={shareProblem} onRetry={() => void startShare()} onDismiss={() => setShareProblem(undefined)}/>}
            {devicesOpen && <DevicesDialog showCamera={leg.capabilities.has('camera')} preview={<CameraPreview/>} onClose={() => setDevicesOpen(false)}/>}
            {howTo && <HowToAllowDialog kind={howTo} onClose={() => setHowTo(undefined)}/>}
            {invite && confirmingInvite && (
                <InviteConfirmDialog
                    invite={invite}
                    onCancel={() => setConfirmingInvite(false)}
                    onConfirm={() => {
                        setConfirmingInvite(false);
                        groupActions.acceptInvite(expanded);
                    }}
                />
            )}
        </CallSurfaceBoundary>
        </ReliefScope>
    );
};
